import moment from 'moment';
import { CustomIntervalEntity } from '../../apis/custom-interval/custom-interval-entity';
import { ScheduleRowEntity } from '../../apis/schedule/schedule-row-entity';
import { SerieSettingEntity } from '../../apis/serie/serie-setting-entity';
import { OpponentsViewModel } from './opponents-view-model';
import { VenueEntity } from '../../apis/venue/venue-entity';
import { GameRow } from './game-row';
import { TeamGame, TeamDropInfo } from './team-game';
import { TeamSettingEntity } from '../../apis/team/team-setting-entity';
import { VenueModel } from './venue-model';
import { ScheduleFieldEntity } from '../../apis/schedule/schedule-field-entity';

export class PresentScheduleViewModel {
    private static readonly _emptyTeams: TeamSettingEntity[] = [];

    public venueModels: VenueModel[] = [];
    public gameRows: GameRow[] = [];
    public opponentList: OpponentsViewModel[] = [];
    private _scheduleRows: ScheduleRowEntity[] = [];
    private _serie: SerieSettingEntity = null;
    private _customIntervals: CustomIntervalEntity[] = [];

    public get fields(): number {
        return this.venueModels.length;
    }

    public initialize(games: ScheduleRowEntity[], serie: SerieSettingEntity, customIntervals: CustomIntervalEntity[], venues: VenueEntity[], fields: ScheduleFieldEntity[]): void {
        this._serie = serie;
        if (!games || !serie || !customIntervals) {
            return;
        }

        this._customIntervals = customIntervals;
        this._scheduleRows.clear();
        this._scheduleRows.addRange(games);
        this.venueModels = this._buildVenueModels(games, venues || [], fields || []);
        this._setup();
    }

    private _buildVenueModels(games: ScheduleRowEntity[], venues: VenueEntity[], fields: ScheduleFieldEntity[]): VenueModel[] {
        const allFieldIds = games.select(g => g.fieldId).distinct<number>().orderBy(f => f);
        return allFieldIds.select(fieldId => {
            const fieldEntry = fields.find(f => f.fieldNumber === fieldId);
            if (fieldEntry?.venueId != null) {
                const venue = venues.firstOrDefault(v => v.id === fieldEntry.venueId);
                return new VenueModel(fieldId, venue || null, fieldEntry.venueId);
            }
            return new VenueModel(fieldId, null);
        });
    }

    private _setup(): void {
        this.gameRows.clear();

        const customIntervals = this._customIntervals.slice();
        let startTime = this._serie.startTime.toMoment();

        // Custom rows before first game
        const customRowsBeforeFirstGame = customIntervals.where(ci => ci.afterInterval === -1);
        for (const customInterval of customRowsBeforeFirstGame) {
            const customRow = new GameRow(-1, startTime.subtract(customInterval.duration, 'minutes'), customInterval.duration, true);
            customRow.message = customInterval.text;
            startTime.add(customInterval.duration, 'minutes');
            this.gameRows.add(customRow);
            customIntervals.remove(customInterval);
        }

        const matchLengthInMinutes = this._serie.periods * this._serie.periodLength + this._serie.periodPause * (this._serie.periods - 1);
        const sortedRows = this._scheduleRows.orderBy(g => g.interval);
        const intervalCount = sortedRows.any() ? sortedRows.lastOrDefault().interval + 1 : 0;

        for (let currentInterval = 0; currentInterval < intervalCount; currentInterval++) {
            const rows = this._scheduleRows.where(s => s.interval === currentInterval);
            const gameRow = new GameRow(currentInterval, startTime, matchLengthInMinutes, true);
            this.gameRows.add(gameRow);

            for (const venueModel of this.venueModels) {
                let game = rows != null ? rows.firstOrDefault(g => g.fieldId === venueModel.fieldId) : null;
                if (game == null) {
                    game = new ScheduleRowEntity();
                    game.fieldId = venueModel.fieldId;
                    game.interval = currentInterval;
                    game.homeTeam = ScheduleRowEntity.freeSlotTeamName;
                    game.opponentTeam = ScheduleRowEntity.freeSlotTeamName;
                }

                startTime = moment(gameRow.endTime);
                const teamGame = new TeamGame(game, this.fields, (_dropData: TeamDropInfo) => undefined, PresentScheduleViewModel._emptyTeams);
                gameRow.addGame(teamGame);
            }

            let addedCustomInterval = false;
            for (const customInterval of customIntervals) {
                if (customInterval.afterInterval === currentInterval) {
                    const customRow = new GameRow(currentInterval, startTime, customInterval.duration, true);
                    customRow.message = customInterval.text;
                    startTime.add(customInterval.duration, 'minutes');
                    this.gameRows.add(customRow);
                    addedCustomInterval = true;
                    customIntervals.remove(customInterval);
                }
            }

            if (!addedCustomInterval) {
                startTime.add(this._serie.gamePause, 'minutes');
            }
        }

        const lastRow = this.gameRows.where(g => !g.isCustomRow).lastOrDefault();
        if (lastRow != null) {
            const customRowsAfterLastGame = customIntervals.where(ci => ci.afterInterval >= lastRow.interval).orderBy(ci => ci.afterInterval);
            startTime = moment(lastRow.endTime);
            let nextInterval = lastRow.interval + 1;
            for (const customInterval of customRowsAfterLastGame) {
                const customRow = new GameRow(nextInterval, startTime, customInterval.duration, true);
                customRow.message = customInterval.text;
                this.gameRows.add(customRow);
                startTime.add(customInterval.duration, 'minutes');
                nextInterval++;
                customIntervals.remove(customInterval);
            }
        }

        this.opponentList.clear();
        for (const game of this._scheduleRows) {
            if (game.homeTeam !== ScheduleRowEntity.freeSlotTeamName) {
                let homeTeamList = this.opponentList.firstOrDefault(ol => ol.team === game.homeTeam);
                if (!homeTeamList) {
                    homeTeamList = new OpponentsViewModel(game.homeTeam);
                    this.opponentList.add(homeTeamList);
                }
                homeTeamList.addOpponent(game.opponentTeam);
            }

            if (game.opponentTeam !== ScheduleRowEntity.freeSlotTeamName) {
                let opponentTeamList = this.opponentList.firstOrDefault(ol => ol.team === game.opponentTeam);
                if (!opponentTeamList) {
                    opponentTeamList = new OpponentsViewModel(game.opponentTeam);
                    this.opponentList.add(opponentTeamList);
                }
                opponentTeamList.addOpponent(game.homeTeam);
            }
        }
    }
}

